/**
 * Shows Horizon's native header after the custom Premium-header leaves the viewport.
 * Hides it again as soon as any part of the custom header re-enters view.
 */
const CUSTOM_SELECTOR = '[data-default-custom-header]';
const NATIVE_SELECTOR = '#header-group > .header-section';
const VISIBLE_CLASS = 'is-scroll-header-visible';
const EDITOR_CLASS = 'is-scroll-header-editor';

class DualHeaderScroll {
  constructor() {
    /** @type {Element | null} */
    this.custom = null;
    /** @type {HTMLElement | null} */
    this.native = null;
    /** @type {IntersectionObserver | null} */
    this.observer = null;
    this.editorForced = false;
    this.boundOnSelect = this.onSectionSelect.bind(this);
    this.boundOnDeselect = this.onSectionDeselect.bind(this);
    this.boundOnLoad = this.onSectionLoad.bind(this);
    this.boundOnUnload = this.onSectionUnload.bind(this);

    this.capture();
    if (!this.custom || !this.native) return;

    this.native.setAttribute('data-scroll-native-header', '');
    this.setVisible(this.isCustomFullyOffscreen());
    this.observe();
    this.bindEditor();
  }

  capture() {
    this.custom = document.querySelector(CUSTOM_SELECTOR);
    this.native = document.querySelector(NATIVE_SELECTOR);
  }

  isCustomFullyOffscreen() {
    if (!this.custom) return false;
    const rect = this.custom.getBoundingClientRect();
    return rect.bottom <= 0 || rect.top >= window.innerHeight;
  }

  setVisible(visible) {
    if (!this.native || this.editorForced) return;

    const show = Boolean(visible);
    this.native.classList.toggle(VISIBLE_CLASS, show);

    if (show) {
      this.closeCustomMobileMenu();
      this.native.removeAttribute('aria-hidden');
      this.native.removeAttribute('inert');
    } else {
      this.native.setAttribute('aria-hidden', 'true');
      this.native.setAttribute('inert', '');
      this.blurFocusInside(this.native);
    }
  }

  closeCustomMobileMenu() {
    if (!this.custom) return;
    const menu = this.custom.querySelector('.header__mobile-menu.active');
    const closeBtn = this.custom.querySelector('.header__mobile-close');
    if (menu instanceof HTMLElement && closeBtn instanceof HTMLElement) {
      closeBtn.click();
    }
  }

  blurFocusInside(root) {
    const active = document.activeElement;
    if (active instanceof HTMLElement && root.contains(active)) {
      active.blur();
    }
  }

  observe() {
    this.observer?.disconnect();
    if (!this.custom) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.setVisible(!entry.isIntersecting);
      },
      { root: null, threshold: 0, rootMargin: '0px' }
    );

    this.observer.observe(this.custom);
  }

  isNativeSectionTarget(target) {
    return target instanceof Element && target.matches(NATIVE_SELECTOR);
  }

  onSectionSelect(event) {
    if (!this.isNativeSectionTarget(event.target)) return;
    this.editorForced = true;
    this.native?.classList.add(EDITOR_CLASS, VISIBLE_CLASS);
    this.native?.removeAttribute('aria-hidden');
    this.native?.removeAttribute('inert');
  }

  onSectionDeselect(event) {
    if (!this.isNativeSectionTarget(event.target)) return;
    this.editorForced = false;
    this.native?.classList.remove(EDITOR_CLASS);
    this.setVisible(this.isCustomFullyOffscreen());
  }

  onSectionLoad() {
    this.capture();
    if (!this.custom || !this.native) return;
    this.native.setAttribute('data-scroll-native-header', '');
    this.observe();
    if (!this.editorForced) {
      this.setVisible(this.isCustomFullyOffscreen());
    }
  }

  onSectionUnload() {
    this.observer?.disconnect();
    this.observer = null;
  }

  bindEditor() {
    if (!window.Shopify?.designMode) return;
    document.addEventListener('shopify:section:select', this.boundOnSelect);
    document.addEventListener('shopify:section:deselect', this.boundOnDeselect);
    document.addEventListener('shopify:section:load', this.boundOnLoad);
    document.addEventListener('shopify:section:unload', this.boundOnUnload);
  }

  destroy() {
    this.observer?.disconnect();
    this.observer = null;
    document.removeEventListener('shopify:section:select', this.boundOnSelect);
    document.removeEventListener('shopify:section:deselect', this.boundOnDeselect);
    document.removeEventListener('shopify:section:load', this.boundOnLoad);
    document.removeEventListener('shopify:section:unload', this.boundOnUnload);
  }
}

function startDualHeaderScroll() {
  if (window.__dualHeaderScroll instanceof DualHeaderScroll) {
    window.__dualHeaderScroll.destroy();
  }
  window.__dualHeaderScroll = new DualHeaderScroll();
}

startDualHeaderScroll();
